import { uiText } from '../i18n/uiText';
import React, { useEffect, useMemo, useRef, useState } from "react";
import { auditPlansApi, companiesApi, getApiErrorMessage } from "../api";
import { formatAuditType, formatStatus } from "../utils/formatters";
import { CalendarDays } from 'lucide-react';
import PageIntro from '../components/ui/PageIntro';
import {useAuth} from '../store/AuthContext';
import useRecordFormDraft from '../hooks/useRecordFormDraft';
import DraftRecoveryNotice from '../components/common/DraftRecoveryNotice';
import SaveStatus from '../components/common/SaveStatus';

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

const emptyItem = {
  audit_type: "",
  audit_object: "",
  object_category: "",
  criticality: "medium",
  audit_goal: "",
  planned_month: "",
  planned_duration_days: "",
  audited_company_id: "",
};

const createEmptyForm = () => ({
  number: `Ф-АУД-01/${new Date().getFullYear()}`,
  year: new Date().getFullYear(),
  status: "draft",
  approved_at: "",
  customer_responsible: "",
  comment: "",
  items: [{ ...emptyItem }],
});

function toDateInput(value) {
  if (!value) return "";
  return String(value).slice(0, 10);
}

export default function AuditPlansPage() {
  const {user}=useAuth();
  const draft=useRecordFormDraft(user?.id,'registry','annual-plan',createEmptyForm());
  const {buffer,form,setForm,recordId:editingId}=draft;
  const busy=useRef(false);
  const [loaded,setLoaded]=useState(false);
  const [plans, setPlans] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [pRes, cRes] = await Promise.all([auditPlansApi.list(), companiesApi.list()]);
      setPlans(Array.isArray(pRes.data) ? pRes.data : []);
      setCompanies(Array.isArray(cRes.data) ? cRes.data : []);
      setLoaded(true);
    } catch (e) {
      setError(getApiErrorMessage(e));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const reset = async() => {
    if(busy.current)return;
    if(await draft.begin(null,createEmptyForm())) setError("");
  };

  const updateItem = (idx, field, value) => {
    setForm((prev) => ({
      ...prev,
      items: prev.items.map((it, i) => (i === idx ? { ...it, [field]: value } : it)),
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if(busy.current || buffer.recovery || !loaded)return;
    if(editingId && !plans.some(plan=>String(plan.id)===String(editingId))){setError(uiText("План больше не доступен. Черновик сохранён; новая запись автоматически не создаётся."));return;}
    busy.current=true;
    const snapshot=buffer.snapshot();
    setSaving(true);
    setError("");
    try {
      const payload = {
        ...form,
        year: Number(form.year),
        items: form.items.map((x) => ({
          ...x,
          planned_duration_days: x.planned_duration_days ? Number(x.planned_duration_days) : null,
          audited_company_id: x.audited_company_id ? Number(x.audited_company_id) : null,
        })),
      };
      if (editingId) {
        await auditPlansApi.update(editingId, payload);
      } else {
        await auditPlansApi.create(payload);
      }
      draft.saved(snapshot);
      if(buffer.active) await load();
    } catch (e2) {
      if(buffer.active) setError(getApiErrorMessage(e2));
    } finally {
      busy.current=false;
      setSaving(false);
    }
  };

  const exportPdf = async (plan) => {
    try {
      const res = await auditPlansApi.pdf(plan.id);
      downloadBlob(res.data, `${plan.number || `audit_plan_${plan.id}`}.pdf`);
    } catch (e) {
      setError(getApiErrorMessage(e));
    }
  };

  const startEdit = async(plan) => {
    if(busy.current)return;
    if(!await draft.begin(plan.id,{
      number: plan.number || "",
      year: plan.year || new Date().getFullYear(),
      status: plan.status || "draft",
      approved_at: toDateInput(plan.approved_at),
      customer_responsible: plan.customer_responsible || "",
      comment: plan.comment || "",
      items: (plan.items || []).length
        ? plan.items.map((item) => ({
            audit_type: item.audit_type || "",
            audit_object: item.audit_object || "",
            object_category: item.object_category || "",
            criticality: item.criticality || "medium",
            audit_goal: item.audit_goal || "",
            planned_month: item.planned_month || "",
            planned_duration_days: item.planned_duration_days || "",
            audited_company_id: item.audited_company_id ? String(item.audited_company_id) : "",
          }))
        : [{ ...emptyItem }],
    }))return;
    setError('');
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const totals = useMemo(() => {
    const totalItems = plans.reduce((acc, plan) => acc + (plan.items?.length || 0), 0);
    const approved = plans.filter((x) => x.status === "approved").length;
    return { totalItems, approved };
  }, [plans]);

  return (
    <div className="workspace-page">
      <PageIntro
        icon={CalendarDays}
        title={uiText("Ф-АУД-01 · Годовой план аудиторской деятельности")}
        description={uiText("Документарная форма плана-графика: год, утверждение, ответственный и перечень аудитов с критичностью, целью и длительностью.")}
      />

      <div className="workspace-stats-grid audit-plan-stats" style={{ marginBottom: 16 }}>
        <div className="workspace-stat"><div className="workspace-stat-value">{plans.length}</div><div className="workspace-stat-label">{uiText("Планов")}</div></div>
        <div className="workspace-stat"><div className="workspace-stat-value">{totals.approved}</div><div className="workspace-stat-label">{uiText("Утверждено")}</div></div>
        <div className="workspace-stat"><div className="workspace-stat-value">{totals.totalItems}</div><div className="workspace-stat-label">{uiText("Строк в плане")}</div></div>
      </div>

      {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}
      <DraftRecoveryNotice buffer={buffer} label={draft.recoveryId ? uiText("годовой план №{v0}", {v0: (draft.recoveryId)}) : uiText("новый годовой план")} disabled={saving || !loaded}/>
      <SaveStatus saving={saving} error={error} recovery={buffer.recovery} dirty={buffer.count>0}/>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div className="card-title">{editingId ? uiText("Редактирование плана") : uiText("Новый план")}</div>
          <button className="btn btn-outline" type="button" disabled={saving} onClick={reset}>{editingId ? uiText("Новый документ") : uiText("Очистить форму")}</button>
        </div>
        <div className="card-body">
          <form onSubmit={submit} style={{ display: "grid", gap: 12 }}>
            <fieldset className="ap-form-lock" disabled={saving || !!buffer.recovery || !loaded}>
            <div className="form-row">
              <div className="form-group"><label className="form-label">{uiText("Номер плана")}</label><input className="form-control" value={form.number} onChange={e => setForm({ ...form, number: e.target.value })} /></div>
              <div className="form-group"><label className="form-label">{uiText("Год")}</label><input className="form-control" type="number" value={form.year} onChange={e => setForm({ ...form, year: e.target.value })} /></div>
              <div className="form-group"><label className="form-label">{uiText("Статус")}</label><select className="form-control" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}><option value="draft">{uiText("Черновик")}</option><option value="approved">{uiText("Утвержден")}</option><option value="archived">{uiText("Архив")}</option></select></div>
            </div>
            <div className="form-row">
              <div className="form-group"><label className="form-label">{uiText("Дата утверждения")}</label><input className="form-control" type="date" value={form.approved_at} onChange={e => setForm({ ...form, approved_at: e.target.value })} /></div>
              <div className="form-group"><label className="form-label">{uiText("Ответственный от заказчика")}</label><input className="form-control" value={form.customer_responsible} onChange={e => setForm({ ...form, customer_responsible: e.target.value })} /></div>
            </div>
            <div className="form-group"><label className="form-label">{uiText("Комментарий")}</label><textarea className="form-control" rows={2} value={form.comment} onChange={e => setForm({ ...form, comment: e.target.value })} /></div>

            <div className="card" style={{ border: "1px solid #eee" }}>
              <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div className="card-title">{uiText("Перечень аудитов")}</div>
                <button type="button" className="btn btn-outline" onClick={() => setForm(prev => ({ ...prev, items: [...prev.items, { ...emptyItem }] }))}>{uiText("Добавить строку")}</button>
              </div>
              <div className="card-body table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>{uiText("Тип аудита")}</th>
                      <th>{uiText("Объект аудита")}</th>
                      <th>{uiText("Категория")}</th>
                      <th>{uiText("Критичность")}</th>
                      <th>{uiText("Цель")}</th>
                      <th>{uiText("Месяц")}</th>
                      <th>{uiText("Длит., дней")}</th>
                      <th>{uiText("Компания")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.items.map((item, idx) => (
                      <tr key={idx}>
                        <td><input className="form-control" value={item.audit_type} onChange={e => updateItem(idx, "audit_type", e.target.value)} /></td>
                        <td><input className="form-control" value={item.audit_object} onChange={e => updateItem(idx, "audit_object", e.target.value)} /></td>
                        <td><input className="form-control" value={item.object_category} onChange={e => updateItem(idx, "object_category", e.target.value)} /></td>
                        <td><select className="form-control" value={item.criticality} onChange={e => updateItem(idx, "criticality", e.target.value)}><option value="high">{uiText("Высокая")}</option><option value="medium">{uiText("Средняя")}</option><option value="low">{uiText("Низкая")}</option></select></td>
                        <td><input className="form-control" value={item.audit_goal} onChange={e => updateItem(idx, "audit_goal", e.target.value)} /></td>
                        <td><input className="form-control" value={item.planned_month} onChange={e => updateItem(idx, "planned_month", e.target.value)} placeholder={uiText("Март")} /></td>
                        <td><input className="form-control" value={item.planned_duration_days} onChange={e => updateItem(idx, "planned_duration_days", e.target.value)} /></td>
                        <td><select className="form-control" value={item.audited_company_id} onChange={e => updateItem(idx, "audited_company_id", e.target.value)}><option value="">—</option>{companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button className="btn btn-primary" type="submit" disabled={saving}>{saving ? uiText("Сохранение...") : editingId ? uiText("Сохранить план") : uiText("Сформировать план")}</button>
            </div>
            </fieldset>
          </form>
        </div>
      </div>

      <div className="card">
        <div className="card-header"><div className="card-title">{uiText("Реестр планов")}</div></div>
        <div className="card-body" style={{ display: "grid", gap: 12 }}>
          {plans.map((plan) => (
            <div key={plan.id} className="card" style={{ border: "1px solid #eee" }}>
              <div className="card-body" style={{ display: "grid", gap: 8 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
                  <div>
                    <div style={{ fontWeight: 700 }}>{plan.number} · {plan.year}</div>
                    <div style={{ fontSize: 13, color: "#666" }}>{plan.customer_responsible || uiText("Ответственный не указан")}{plan.approved_at ? uiText(" · утвержден {v0}", {v0: (new Date(plan.approved_at).toLocaleDateString("ru-RU"))}) : ""}</div>
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <span className={`status-badge ${plan.status}`}>{formatStatus(plan.status, 'plan')}</span>
                    <button className="btn btn-outline" type="button" onClick={() => exportPdf(plan)}>PDF</button>
                    <button className="btn btn-outline" type="button" onClick={() => startEdit(plan)}>{uiText("Редактировать")}</button>
                  </div>
                </div>
                <div className="table-wrap">
                  <table className="table">
                    <thead><tr><th>{uiText("Тип")}</th><th>{uiText("Объект")}</th><th>{uiText("Критичность")}</th><th>{uiText("Месяц")}</th><th>{uiText("Дней")}</th></tr></thead>
                    <tbody>
                      {(plan.items || []).map((item) => (
                        <tr key={item.id}><td>{formatAuditType(item.audit_type)}</td><td>{item.audit_object}</td><td>{{ high: uiText("Высокая"), medium: uiText("Средняя"), low: uiText("Низкая") }[item.criticality] || item.criticality || "—"}</td><td>{item.planned_month || "—"}</td><td>{item.planned_duration_days || "—"}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
